import { upperCase } from 'lodash';
import React from 'react';
import classNames from 'classnames';
import List from '../../../components/list/List';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { orderV2Selectors } from '../../../redux/selectors/order.v2';
import text from '../../../text';
import { usePaymentContext } from '../../hooks/usePaymentContext';

export const InvoiceTotal: React.FC<{
  isRfq?: boolean;
  isApproximate?: boolean;
}> = ({ isRfq = false, isApproximate = false }) => {
  const { jobId, formatPrice, asMember } = usePaymentContext();
  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );

  if (!quote) return null;

  const totals = isRfq
    ? (quote.getQuoteTotals ?? quote.rfq.totals)
    : (quote.buyNowTotals ?? quote.nonRfq.totals);
  const composition = isRfq ? quote.rfq.composition : quote.nonRfq.composition;
  const totalGross = totals.totalGross;
  const totalNet = totals.totalNet;

  const hasNoRfq = quote.rfq.totals.totalGross === 0;
  const hasNoNonRfq = quote.nonRfq.totals.totalGross === 0;
  const fullPriceText = `${hasNoRfq ? '' : `${upperCase(text('buyNow'))} `}${upperCase(text('fullPriceTotal'))} ${text('excGst')}`;
  const discountsText = `${hasNoRfq ? '' : `${upperCase(text('buyNow'))} `}${upperCase(text('discounts'))}`;

  const totalPrefix =
    hasNoRfq || hasNoNonRfq
      ? ''
      : `${upperCase(isRfq ? text('getQuote') : text('buyNow'))} `;

  const getGstText = (displayName: string) =>
    `${totalPrefix}${upperCase(text(displayName))}`;
  const subtotalText = `${totalPrefix}${text('subtotal')}`;
  const grandTotalText = `${totalPrefix}${text('total')}`;

  const buyNowFullPrice =
    totals.baseBeforeDiscounts === 'TBC'
      ? text('toBeConfirmed')
      : formatPrice(totals.baseBeforeDiscounts);

  const nonRfqDiscounts = quote.buyNowTotals
    ? totals.discounts
    : composition.data_in_database?.totals.discounts || [];
  const compositionOnlyShowsMembership = Object.keys(composition).every(
    (k) => k === 'membership'
  );

  return (
    <List
      isPaddingless
      className={classNames('InvoiceTotal', {
        isRfq,
      })}
    >
      {!isRfq && !compositionOnlyShowsMembership && (
        <>
          {/* @ts-expect-error List.Item is not typed */}
          <List.Item
            description={fullPriceText}
            value={buyNowFullPrice}
            key='buy-now-full-price'
            className='buy-now-full-price font-14 font-italic text-strikethrough font-weight--400-important font-grey--3'
          />

          {nonRfqDiscounts.map((discount, i) => (
            // @ts-expect-error List.Item is not typed
            <List.Item
              className={classNames(
                'discount pl-3 pt-1 font-italic font-weight--400-important',
                {
                  'font-pink': asMember,
                  'font-orange': !asMember,
                  'no-border-bottom': i !== (nonRfqDiscounts.length || 0) - 1,
                  'last-row': i === (nonRfqDiscounts.length || 0) - 1,
                }
              )}
              key={i}
              description={discount.metadata.displayName}
              value={`—${formatPrice(discount.amount)}`}
            />
          ))}

          {/* @ts-expect-error List.Item is not typed */}
          <List.Item
            description={discountsText}
            value={`—${formatPrice(totals.discountsTotal)}`}
            key='invoice-discounts'
            className={classNames('discounts', {
              'font-pink': asMember,
              'font-orange': !asMember,
            })}
          />
        </>
      )}

      {/* @ts-expect-error List.Item is not typed */}
      <List.Item
        className={classNames('no-border-bottom', {
          'font-grey--3 font-weight--400-important': isRfq,
        })}
        description={subtotalText}
        value={
          totalGross === 'TBC'
            ? text('toBeConfirmed')
            : `${isApproximate ? '~' : ''}${formatPrice(totalGross)}`
        }
        key='invoice-subtotal'
      />

      {totalGross === 'TBC' ? (
        // @ts-expect-error List.Item is not typed
        <List.Item
          description={text('gst')}
          value={text('toBeConfirmed')}
          key='invoice-gst-tbc'
          className={classNames('invoice-totals', {
            'font-grey--3': isRfq,
          })}
        />
      ) : (
        totals.taxes.map((tax, i) => (
          // @ts-expect-error List.Item is not typed
          <List.Item
            className={classNames('invoice-totals', {
              'font-grey--3': isRfq,
            })}
            key={i}
            description={getGstText(tax.metadata.displayName)}
            value={`${isApproximate ? '~' : ''}${formatPrice(tax.amount as number)}`}
          />
        ))
      )}

      {/*  @ts-expect-error List.Item is not typed */}
      <List.Item
        className={classNames('invoice-totals', {
          'font-grey--3': isRfq,
        })}
        isHighlighted
        description={grandTotalText}
        key='invoice-total'
        value={
          totalNet === 'TBC'
            ? text('toBeConfirmed')
            : `${isApproximate ? '~' : ''}${formatPrice(totalNet)}`
        }
      />
    </List>
  );
};
